import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useReducedMotion } from 'framer-motion';
import { ArrowLeft, Layers } from 'lucide-react';
import { usePlatformSettings } from '../../hooks/usePlatformSettings';
import { LanguageSwitcher } from './LanguageSwitcher';

/** Shared chrome for every auth/onboarding screen (login, register,
 *  forgot/reset password, verify email, accept invite). Mirrors the
 *  Home landing design system: pure-black canvas, animated aurora,
 *  Apple-style glass cards, neon gradient brand text and the lime
 *  primary CTA. Pages only supply the inner card content. */

/** Tailwind class bundle for glass inputs used across auth forms.
 *  Exported so every form field renders identically. */
export const authInputClass =
  'block w-full rounded-xl border border-white/10 bg-white/[0.04] py-3 pl-11 pr-3 text-white placeholder-white/30 transition focus:border-cyan-300/50 focus:outline-none focus:ring-2 focus:ring-cyan-300/20 disabled:opacity-60';

/** Glass card wrapper (with a soft neon halo) for the auth content. */
export const AuthCard: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <div className="relative">
    <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-lime-300/20 via-cyan-300/15 to-violet-500/25 opacity-70 blur-2xl" />
    <div className={`glass-skin relative rounded-[1.75rem] p-8 ${className}`}>{children}</div>
  </div>
);

interface AuthShellProps {
  children: React.ReactNode;
  /** When set, renders the brand logo + gradient title + this tagline. */
  tagline?: string;
  /** Optional top-left back button. */
  back?: { label: string; to: string };
  /** Hide the bottom footer (default shown). */
  hideFooter?: boolean;
}

export const AuthShell: React.FC<AuthShellProps> = ({ children, tagline, back, hideFooter }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const { settings } = usePlatformSettings();
  const shouldReduceMotion = useReducedMotion();

  const brandName = settings.logo_text || settings.site_name || 'HitzLink';

  return (
    <div className="landing-shell relative min-h-screen overflow-hidden bg-growth-ink text-white">
      {/* Animated aurora backdrop — same language as the Home hero. */}
      {!shouldReduceMotion && (
        <div className="landing-aurora" aria-hidden="true">
          <div className="landing-aurora__blob animate-aurora left-[-12%] top-[-10%] h-[34rem] w-[34rem] bg-cyan-400/30" />
          <div className="landing-aurora__blob animate-aurora-slow right-[-14%] top-[0%] h-[40rem] w-[40rem] bg-violet-500/30" />
          <div className="landing-aurora__blob animate-aurora left-[30%] bottom-[-12%] h-[28rem] w-[28rem] bg-lime-300/15" style={{ animationDelay: '-6s' }} />
        </div>
      )}

      {back && (
        <button
          type="button"
          onClick={() => navigate(back.to)}
          className="glass-skin absolute left-5 top-5 z-20 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white/80 transition hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          {back.label}
        </button>
      )}

      <div className="absolute right-5 top-5 z-20">
        <LanguageSwitcher size="sm" variant="dark" />
      </div>

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-24">
        <div className="w-full max-w-md">
          {tagline && (
            <div className="mb-8 text-center">
              <div className="mb-5 inline-flex items-center justify-center">
                {settings.logo_url ? (
                  <img
                    src={settings.logo_url}
                    alt={brandName}
                    className="h-14 w-auto object-contain"
                  />
                ) : (
                  <div className="grid h-14 w-14 place-items-center overflow-hidden rounded-2xl bg-gradient-to-br from-lime-300 via-cyan-300 to-violet-500 shadow-glow">
                    <Layers className="h-7 w-7 text-growth-ink" />
                  </div>
                )}
              </div>
              <h1 className="text-4xl font-black tracking-tight">
                <span className="landing-gradient-text">{brandName}</span>
              </h1>
              <p className="mt-3 text-base text-white/[0.6]">{tagline}</p>
            </div>
          )}

          {children}
        </div>
      </div>

      {!hideFooter && (
        <footer className="absolute inset-x-0 bottom-0 z-10 border-t border-white/10 px-4 py-6 backdrop-blur-sm">
          <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 text-sm text-white/[0.45] sm:flex-row">
            <div>{t('common.footerCopyright', { year: new Date().getFullYear() })}</div>
            <div className="flex gap-6">
              <button type="button" onClick={() => navigate('/legal')} className="transition hover:text-white">
                {t('common.footerTerms')}
              </button>
              <a href="mailto:support@hitzlink.com" className="transition hover:text-white">
                {t('common.footerSupport')}
              </a>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};
